//short-trend-chart.js
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
	ResponsiveContainer,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ComposedChart,
	ReferenceLine,
} from 'recharts'
import {
	CONST_DATES,
	CONST_ROMAN_FACTORS,
	CONST_ROMAN_KPI,
} from '../../../../constants/general'
import { ICONS_CHARTS } from '../../../../constants/icons'
const CustomLabel = ({ viewBox, value }) => {
	const { x, y } = viewBox
	return (
		<text x={x} y={y} dy={-4} fill='red' fontSize={10} textAnchor='start'>
			{`Leads Target = ${value}`}
		</text>
	)
}

const CustomTooltip = ({ active, payload, label }) => {
	if (active && payload && payload.length) {
		const total = payload.reduce((acc, curr) => acc + curr.value, 0)
		return (
			<div className='outline-none p-2 bg-romanLight3 dark:bg-romanDark3 bg-opacity-50 dark:bg-opacity-50'>
				<p className='label'>{`Total on ${label} : ${total}`}</p>
				{payload.map((entry, index) => (
					<p
						style={{ color: entry.color }}
						key={`item-${index}`}
					>{`${entry.name} : ${entry.value}`}</p>
				))}
			</div>
		)
	}
	return null
}

const CustomLegend = (props) => {
	const { payload } = props

	return (
		<div className='flex flex-col space-y-3 text-xs'>
			<div className='flex space-x-3 items-center justify-center'>
				{/* <div className='text-lg font-semibold'>{ICONS_CHARTS.bar2}</div> */}
				<div className='flex space-x-2'>
					{payload.map((entry, index) => (
						<div key={`item-${index}`} style={{ color: entry.color }}>
							{entry.value}
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

const ShortChartTrend = ({ rawData, eventTypeColors }) => {
	const [chartData, setChartData] = useState([])
	const [timePeriod, setTimePeriod] = useState(CONST_DATES.week)
	const [target, setTarget] = useState(
		Math.ceil(
			CONST_ROMAN_KPI.salesThroughput.target /
				CONST_ROMAN_FACTORS.leadToSaleConversion,
		),
	)

	const groupDataByTimePeriod = (data, period) => {
		return data.reduce((acc, curr) => {
			let dateKey

			switch (period) {
				case CONST_DATES.day:
					dateKey = moment(curr.date).format('YYYY-MM-DD')
					break
				case CONST_DATES.week:
					dateKey = moment(curr.date).startOf('week').format('YYYY-MM-DD')
					break
				case CONST_DATES.month:
					dateKey = moment(curr.date).startOf('month').format('YYYY-MM-DD')
					break
				default:
					throw new Error('Invalid time period')
			}

			if (!acc[dateKey]) {
				acc[dateKey] = {
					chartDay: dateKey,
					'Lead Created': 0,
					'Digital Lead': 0,
					'Salesman Lead': 0,
				}
			}

			const mappedEventType = CONST_ROMAN_KPI[curr.eventType]?.chartName
			if (mappedEventType) {
				acc[dateKey][mappedEventType] = (acc[dateKey][mappedEventType] || 0) + 1
			}

			return acc
		}, {})
	}

	useEffect(() => {
		if (rawData && rawData.length > 0) {
			const groupedData = groupDataByTimePeriod(rawData, timePeriod)
			setChartData(Object.values(groupedData))
			switch (timePeriod) {
				case CONST_DATES.day:
					setTarget(
						Math.ceil(
							CONST_ROMAN_KPI.salesThroughput.target /
								CONST_ROMAN_FACTORS.leadToSaleConversion /
								7,
						),
					)

					break
				case CONST_DATES.week:
					setTarget(
						Math.ceil(
							CONST_ROMAN_KPI.salesThroughput.target /
								CONST_ROMAN_FACTORS.leadToSaleConversion,
						),
					)
					break
				case CONST_DATES.month:
					setTarget(
						Math.ceil(
							(CONST_ROMAN_KPI.salesThroughput.target /
								CONST_ROMAN_FACTORS.leadToSaleConversion /
								12) *
								52,
						),
					)
					break

				default:
					break
			}
		} else {
			setChartData([])
			setTarget(
				Math.ceil(
					CONST_ROMAN_KPI.salesThroughput.target /
						CONST_ROMAN_FACTORS.leadToSaleConversion,
				),
			)
		}
	}, [rawData, timePeriod])

	return (
		<div style={{ width: '100%', height: 300 }} className='grow mt-10'>
			<div className='flex space-x-2 py-1 px-2 justify-end '>
				<button
					className={`rounded-full py-1 px-2 border-1 ${
						timePeriod === CONST_DATES.day
							? 'border-romanRed2  '
							: 'border-romanLightTextInactive'
					}  ${
						timePeriod === CONST_DATES.day
							? 'dark:border-romanGold1'
							: 'dark:border-romanDarkBorder1'
					}`}
					onClick={() => setTimePeriod(CONST_DATES.day)}
				>
					Day
				</button>
				<button
					className={`rounded-full py-1 px-2 border-1 ${
						timePeriod === CONST_DATES.week
							? 'border-romanRed2  '
							: 'border-romanLightTextInactive'
					}  ${
						timePeriod === CONST_DATES.week
							? 'dark:border-romanGold1'
							: 'dark:border-romanDarkBorder1'
					}`}
					onClick={() => setTimePeriod(CONST_DATES.week)}
				>
					Week
				</button>
				<button
					className={`rounded-full py-1 px-2 border-1 ${
						timePeriod === CONST_DATES.month
							? 'border-romanRed2  '
							: 'border-romanLightTextInactive'
					}  ${
						timePeriod === CONST_DATES.month
							? 'dark:border-romanGold1'
							: 'dark:border-romanDarkBorder1'
					}`}
					onClick={() => setTimePeriod(CONST_DATES.month)}
				>
					Month
				</button>
			</div>
			<ResponsiveContainer>
				<ComposedChart
					data={chartData}
					margin={{
						top: 5,
						right: 30,
						left: 20,
						bottom: 5,
					}}
				>
					<CartesianGrid strokeDasharray='3 3' />
					<XAxis
						dataKey='chartDay'
						tick={{ fontSize: 10 }} // Set the font size for the X-axis labels
						// tickFormatter={(value) => {
						// 	const date = new Date(value)
						// 	return `${date.getMonth() + 1}-${date.getDate()}`
						// }}
					/>
					<YAxis
						domain={[
							0,
							(dataMax) => Math.max(dataMax, Math.ceil(target + 0.1 * dataMax)),
						]}
						tick={{ fontSize: 10 }} // Set the font size for the X-axis labels
					/>
					<Tooltip content={<CustomTooltip />} />
					<Legend content={<CustomLegend />} />

					{eventTypeColors.map((item) => (
						<Bar
							key={item.dataKey}
							dataKey={item.dataKey}
							stackId='a'
							fill={item.color}
						/>
					))}
					<ReferenceLine
						y={target}
						label={<CustomLabel value={target} />}
						stroke='red'
					/>
				</ComposedChart>
			</ResponsiveContainer>
		</div>
	)
}

export default ShortChartTrend
